import React, { useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { Box, SimpleGrid, Text } from '@chakra-ui/react'
import {
  Stack,
  SurfaceCard,
  HStack,
  Typography,
  Tag,
  TTagVariant,
  Tab,
  Button,
  DataGrid,
  TColumnDef,
  useToast,
  Icon,
} from '@pnld/components-web'

import { useProcessing } from '@/api/processing/queries'
import { ROUTES } from '@/config/routes'
import { Status } from '@/types/processing'
import { getErrorMessage } from '@/utils/api'

import {
  STATUS_LABEL,
  DEED_TYPE_LABEL_WITH_LEGACY,
  EDUCATION_STAGE_LABEL,
} from '../constants'
import { FailureRow, parseFailures } from './parseFailures'
import PrintingCard from './parts/printing-card'
import ProcessingCard from './parts/processing-card'

const InfoRow: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <Text fontSize="16.8px" color="brand.neutral.dark_2" noOfLines={1}>
    <Text as="span" fontWeight="bold">
      {label}
    </Text>
    {` ${value}`}
  </Text>
)

const ProcessingDetailsPage: React.FC = () => {
  const toast = useToast()
  const navigate = useNavigate()

  const { id } = useParams<{ id: string }>()
  const processingId = id ? Number(id) : null

  const { data, error, isError } = useProcessing(
    { id: Number(id) },
    { enabled: Boolean(id) }
  )

  useEffect(() => {
    if (isError) {
      toast({ status: 'error', title: getErrorMessage(error) })
      navigate(ROUTES.PROCESSING)
    }
  }, [isError, navigate])

  // PSV-426: com mapa (e resultados ligados a ele), as abas exibem Categoria,
  // Área e Etapas Atendidas. A flag vem do detalhe e não dos filtros, cujo
  // refetch por Estado faria colunas e selects oscilarem. Enquanto o detalhe
  // carrega ela é `false`; se virar `true`, a `key` remonta os cards, para que
  // o estado de filtro não guarde o valor de um select que sumiu (ex.:
  // coComponent). A key também leva o id: a rota `detalhes/:id` reaproveita a
  // página, e sem isso os filtros e a paginação de um processamento valeriam
  // no próximo. No mesmo processamento sem mapa, a key não muda.
  const hasServiceMap = Boolean(data?.inHasServiceMap)
  const cardsKey = `${id}-${hasServiceMap ? 'com-mapa' : 'sem-mapa'}`

  const stStatus = data?.stStatus || Status.PENDENTE
  const isFailed = stStatus === Status.FALHOU

  const statusVariantMap: Record<Status, TTagVariant> = {
    [Status.PENDENTE]: 'neutral',
    [Status.PROCESSANDO]: 'primary',
    [Status.CONCLUIDO]: 'positive',
    [Status.FALHOU]: 'negative',
  }
  const variant = statusVariantMap[stStatus as Status]

  const createdAt = data?.dtCreatedAt
    ? new Date(data.dtCreatedAt).toLocaleString('pt-BR')
    : '-'

  const solicitante = data
    ? `${data.requester.noUserName} | ${data.requester.dsCpf}`
    : '-'

  const arquivosRecebidos = data
    ? [
        data.noFileNameAdherenceReport,
        data.noFileNameSelectionReport,
        data.noFileNameServiceMap,
      ]
        .filter(Boolean)
        .join('; ')
    : '-'

  const failures = useMemo(
    () =>
      parseFailures(data?.dsErrorLog, {
        adherence: data?.noFileNameAdherenceReport ?? '',
        selection: data?.noFileNameSelectionReport ?? '',
        serviceMap: data?.noFileNameServiceMap,
      }),
    [data]
  )

  const failureColumns: Array<TColumnDef<FailureRow>> = useMemo(
    () => [
      {
        accessorKey: 'arquivo',
        header: 'Arquivo',
        enableSorting: false,
        size: 240,
      },
      {
        accessorKey: 'falha',
        header: 'Falhas',
        enableSorting: false,
      },
    ],
    []
  )

  return (
    <Stack spacing={4}>
      <SurfaceCard padding={5}>
        <HStack spacing={4} justifyContent="space-between" mb={4}>
          <Typography variant="h-xs" color="brand.primary.dark_2">
            Processamento
          </Typography>
          <Tag variant={variant} sx={{ textWrap: 'nowrap' }}>
            {STATUS_LABEL[stStatus]}
          </Tag>
        </HStack>

        <Stack spacing={3}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <InfoRow
              label="ID do processamento:"
              value={data ? String(data.coProcessing) : '-'}
            />
            <InfoRow label="Data do processamento:" value={createdAt} />
            <InfoRow label="Solicitante:" value={solicitante} />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            <InfoRow label="Edital:" value={data?.dsNotice || '-'} />
            <InfoRow
              label="Etapa de ensino:"
              value={data ? EDUCATION_STAGE_LABEL[data.nuEducationStage] : '-'}
            />
            <InfoRow
              label="Tipo de obra:"
              value={data ? DEED_TYPE_LABEL_WITH_LEGACY[data.nuDeedType] : '-'}
            />
          </SimpleGrid>

          <InfoRow
            label="Nome do arquivo recebido:"
            value={arquivosRecebidos}
          />
        </Stack>

        <HStack justifyContent="space-between" mt={6}>
          <Button
            variant="tertiary"
            label="Voltar"
            iconName="chevron-left"
            border="pill"
            onClick={() => navigate(ROUTES.PROCESSING)}
          />
        </HStack>
      </SurfaceCard>

      {isFailed && (
        <>
          <Box
            bg="brand.error.light"
            borderRadius="lg"
            p={4}
            display="flex"
            gap={4}
            alignItems="center"
          >
            <Box flexShrink={0} color="brand.error.base">
              <Icon name="times-circle" size="sm" variant="negative" />
            </Box>
            <Stack spacing={0}>
              <Text fontWeight="bold" fontSize="16.8px">
                Foram encontradas falhas nos arquivos enviados!
              </Text>
              <Text fontSize="16.8px">
                Identificámos um ou mais erros nos arquivos submetidos. Por
                favor, corrija os arquivos de acordo com as falhas listadas
                abaixo e tente novamente o envio.
              </Text>
            </Stack>
          </Box>

          <SurfaceCard padding={5}>
            <Typography variant="h-xs" color="brand.primary.dark_2">
              Falhas encontradas
            </Typography>
            <Box mt={4}>
              <DataGrid
                columns={failureColumns}
                data={failures}
                hasPagination={false}
              />
            </Box>
          </SurfaceCard>
        </>
      )}

      {!isFailed && (
        <Tab
          panels={[
            {
              title: 'Tiragem',
              render: () => (
                <PrintingCard
                  key={cardsKey}
                  processingId={processingId}
                  hasServiceMap={hasServiceMap}
                />
              ),
            },
            {
              title: 'Processamento',
              render: () => (
                <ProcessingCard
                  key={cardsKey}
                  processingId={processingId}
                  hasServiceMap={hasServiceMap}
                />
              ),
            },
          ]}
          isFitted
        />
      )}
    </Stack>
  )
}

export default ProcessingDetailsPage
